export type TextMatchMode = "exact" | "normalized" | "whitespace_agnostic";

/** Punctuation/symbol lookups must not fold distinct characters or expand runs. */
export function isLiteralCharacterSearch(query: string): boolean {
  return /^[\p{P}\p{S}]$/u.test(query.trim());
}

export type TextMatchRange = {
  start: number;
  end: number;
};

const MIN_ELLIPSIS_WRAPPER_CONTENT_LENGTH = 20;

/**
 * Removes leading or trailing ellipses that an LLM may add to show that a
 * stored passage was excerpted. Short strings stay unchanged so ordinary
 * punctuation is not treated as an excerpt wrapper.
 */
export function stripOptionalEllipsisWrappers(text: string): string {
  const stripped = text
    .replace(/^(?:\.{3}|\u2026)\s*/, "")
    .replace(/\s*(?:\.{3}|\u2026)$/, "");

  return stripped !== text && stripped.trim().length >= MIN_ELLIPSIS_WRAPPER_CONTENT_LENGTH
    ? stripped
    : text;
}

/** Apostrophe / single-quote characters treated as equivalent when matching manuscript text. */
export const MATCHING_APOSTROPHE_CHARS = "'\u2018\u2019\u201A\u201B\u2032\u0060";
/** Double-quote characters treated as equivalent when matching manuscript text. */
export const MATCHING_DOUBLE_QUOTE_CHARS = '"\u201C\u201D\u201E\u201F\u2033';

function isMatchingApostropheChar(ch: string): boolean {
  return MATCHING_APOSTROPHE_CHARS.includes(ch);
}

function isMatchingDoubleQuoteChar(ch: string): boolean {
  return MATCHING_DOUBLE_QUOTE_CHARS.includes(ch);
}

/** 1:1 fold so curly vs straight quotes compare equal without shifting indices. */
export function foldMatchingQuoteChars(text: string): string {
  let folded = "";
  for (const ch of text) {
    if (isMatchingApostropheChar(ch)) folded += "'";
    else if (isMatchingDoubleQuoteChar(ch)) folded += '"';
    else folded += ch;
  }
  return folded;
}

/** Escape a find/replace needle so curly and straight quotes match each other. */
export function quoteInsensitiveRegexSource(term: string): string {
  let source = "";
  for (const ch of term) {
    if (isMatchingApostropheChar(ch)) {
      source += "['\\u2018\\u2019\\u201A\\u201B\\u2032\\u0060]";
    } else if (isMatchingDoubleQuoteChar(ch)) {
      source += '["\\u201C\\u201D\\u201E\\u201F\\u2033]';
    } else {
      source += ch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }
  }
  return source;
}

function normalizeMatchingChar(ch: string): string {
  const code = ch.charCodeAt(0);
  if (code < 0x80) {
    if (code === 0x20 || (code >= 0x09 && code <= 0x0d)) return " ";
    if (ch === "`") return "'";
    return code >= 0x41 && code <= 0x5a ? String.fromCharCode(code + 0x20) : ch;
  }
  if (/\s/.test(ch)) return " ";
  if (isMatchingApostropheChar(ch)) return "'";
  if (isMatchingDoubleQuoteChar(ch)) return '"';
  return ch
    .replace(/[\u2022\u2023\u25E6\u2043\u2219\u25CF\u25CB\u00B7]/g, "*")
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u2026/g, ".")
    .toLowerCase();
}

function buildNormalizedTextWithSourceIndices(
  source: string,
  mode: Exclude<TextMatchMode, "exact">,
): { normalized: string; sourceIndices: number[] } {
  let normalized = "";
  const sourceIndices: number[] = [];
  let previousWasSpace = false;
  // `normalized.endsWith` would flatten the growing concatenation on every call, which is quadratic.
  let lastAppended = "";

  for (let i = 0; i < source.length; i += 1) {
    const normalizedChar = normalizeMatchingChar(source[i]);

    // Normalized locate is quote-insensitive: curly vs straight and optional closing quotes.
    if (normalizedChar === '"') {
      continue;
    }

    if (mode === "whitespace_agnostic" && normalizedChar === " ") {
      continue;
    }

    if (mode === "whitespace_agnostic" && normalizedChar === "." && lastAppended.endsWith(".")) {
      continue;
    }

    if (mode === "whitespace_agnostic" && normalizedChar === "*" && lastAppended.endsWith("*")) {
      continue;
    }

    if (mode === "normalized" && normalizedChar === " ") {
      if (previousWasSpace) continue;
      previousWasSpace = true;
    } else {
      previousWasSpace = false;
    }

    normalized += normalizedChar;
    lastAppended = normalizedChar;
    sourceIndices.push(i);
  }

  if (mode === "normalized" && normalized.length > 0) {
    let start = 0;
    let end = normalized.length;
    while (start < end && normalized[start] === " ") start += 1;
    while (end > start && normalized[end - 1] === " ") end -= 1;
    if (start > 0 || end < normalized.length) {
      normalized = normalized.slice(start, end);
      return { normalized, sourceIndices: sourceIndices.slice(start, end) };
    }
  }

  return { normalized, sourceIndices };
}

/**
 * Wraps a pure `(source) => result` builder so calls repeating one of the last `capacity` sources reuse its
 * result. Callers run many needles against one manuscript, and rebuilding it each time is linear in its
 * length. Selection locate alternates between the client plain body, the live markdown, and its tag-stripped
 * search text, so a single slot is evicted on every switch. The result is shared, so callers must not mutate it.
 */
export function reuseRecentSourceResults<T>(
  build: (source: string) => T,
  capacity = 3,
): (source: string) => T {
  const entries: Array<{ source: string; result: T }> = [];
  return (source) => {
    const index = entries.findIndex((entry) => entry.source === source);
    if (index === 0) return entries[0]!.result;
    const entry = index > 0 ? entries.splice(index, 1)[0]! : { source, result: build(source) };
    entries.unshift(entry);
    if (entries.length > capacity) entries.length = capacity;
    return entry.result;
  };
}

const normalizedHaystackBuilders = {
  normalized: reuseRecentSourceResults((source) => buildNormalizedTextWithSourceIndices(source, "normalized")),
  whitespace_agnostic: reuseRecentSourceResults((source) =>
    buildNormalizedTextWithSourceIndices(source, "whitespace_agnostic")
  ),
};

const foldHaystackQuoteChars = reuseRecentSourceResults(foldMatchingQuoteChars);

function findAllIndexes(haystack: string, needle: string): number[] {
  if (!needle) return [];
  const matches: number[] = [];
  let pos = 0;
  while ((pos = haystack.indexOf(needle, pos)) !== -1) {
    matches.push(pos);
    pos += 1;
  }
  return matches;
}

function isWordChar(ch: string | undefined): boolean {
  return typeof ch === "string" && /[\p{L}\p{N}_]/u.test(ch);
}

/** True when `[start, end)` is bounded by non-word characters (or string edges). */
export function isWholeWordSpan(haystack: string, start: number, end: number): boolean {
  if (start < 0 || end > haystack.length || end <= start) return false;
  return !isWordChar(haystack[start - 1]) && !isWordChar(haystack[end]);
}

/** True when a find/replace needle should match only at word boundaries. */
export function shouldUseWholeWordMatching(needle: string): boolean {
  const trimmed = String(needle ?? "").trim();
  if (!trimmed) return false;
  return /^[\p{L}\p{N}_]+(?:['’\-][\p{L}\p{N}_]+)*(?:\s+[\p{L}\p{N}_]+(?:['’\-][\p{L}\p{N}_]+)*)*$/u.test(trimmed);
}

function chooseBestIndex(
  matches: number[],
  sourceOffsetAtIndex: (index: number) => number,
  hintOffset?: number,
): number {
  if (matches.length === 0) return -1;
  if (matches.length === 1 || hintOffset === undefined) return matches[0];

  let best = matches[0];
  let bestDistance = Math.abs(sourceOffsetAtIndex(best) - hintOffset);
  for (const candidate of matches) {
    const distance = Math.abs(sourceOffsetAtIndex(candidate) - hintOffset);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best;
}

function isWhitespaceChar(ch: string | undefined): boolean {
  return typeof ch === "string" && /\s/.test(ch);
}

function firstNonWhitespaceChar(text: string): string | null {
  for (let i = 0; i < text.length; i += 1) {
    if (!isWhitespaceChar(text[i])) return text[i];
  }
  return null;
}

function lastNonWhitespaceChar(text: string): string | null {
  for (let i = text.length - 1; i >= 0; i -= 1) {
    if (!isWhitespaceChar(text[i])) return text[i];
  }
  return null;
}

function boundaryCharsEquivalent(left: string | undefined, right: string | undefined): boolean {
  if (!left || !right) return false;
  return normalizeMatchingChar(left) === normalizeMatchingChar(right);
}

function isStarLikeChar(ch: string | undefined): boolean {
  return typeof ch === "string" && normalizeMatchingChar(ch) === "*";
}

function countStarLikeChars(text: string): number {
  let count = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (isStarLikeChar(text[i])) count += 1;
  }
  return count;
}

function expandMatchRangeForNeedleBoundaries(
  haystack: string,
  needle: string,
  range: TextMatchRange,
  lowerBound = 0,
): TextMatchRange {
  let start = range.start;
  let end = range.end;

  const leadingWhitespaceNeedle = needle.match(/^\s+/)?.[0].length ?? 0;
  if (leadingWhitespaceNeedle > 0) {
    let consumed = 0;
    while (consumed < leadingWhitespaceNeedle && start > lowerBound && isWhitespaceChar(haystack[start - 1])) {
      start -= 1;
      consumed += 1;
    }
  }

  const trailingWhitespaceNeedle = needle.match(/\s+$/)?.[0].length ?? 0;
  if (trailingWhitespaceNeedle > 0) {
    let consumed = 0;
    while (consumed < trailingWhitespaceNeedle && end < haystack.length && isWhitespaceChar(haystack[end])) {
      end += 1;
      consumed += 1;
    }
  }

  const leadingBoundary = firstNonWhitespaceChar(needle);
  if (
    leadingBoundary
    && start > lowerBound
    && boundaryCharsEquivalent(haystack[start - 1], leadingBoundary)
  ) {
    start -= 1;
  }

  const trailingBoundary = lastNonWhitespaceChar(needle);
  if (
    trailingBoundary
    && end < haystack.length
    && boundaryCharsEquivalent(haystack[end], trailingBoundary)
  ) {
    end += 1;
  }

  const requiredStarLikeCount = countStarLikeChars(needle);
  if (requiredStarLikeCount >= 2) {
    const matchedStarLikeCount = countStarLikeChars(haystack.slice(start, end));
    if (matchedStarLikeCount < requiredStarLikeCount) {
      let expandedStart = start;
      while (
        expandedStart > lowerBound
        && haystack[expandedStart - 1] !== "\n"
        && (isWhitespaceChar(haystack[expandedStart - 1]) || isStarLikeChar(haystack[expandedStart - 1]))
      ) {
        expandedStart -= 1;
      }
      let expandedEnd = end;
      while (
        expandedEnd < haystack.length
        && haystack[expandedEnd] !== "\n"
        && (isWhitespaceChar(haystack[expandedEnd]) || isStarLikeChar(haystack[expandedEnd]))
      ) {
        expandedEnd += 1;
      }
      const expandedSlice = haystack.slice(expandedStart, expandedEnd);
      if (countStarLikeChars(expandedSlice) >= requiredStarLikeCount) {
        start = expandedStart;
        end = expandedEnd;
      }
    }
  }

  return { start, end };
}

export function findTextMatchRange(
  haystack: string,
  needle: string,
  options?: { mode?: TextMatchMode; hintOffset?: number; wholeWord?: boolean },
): TextMatchRange | null {
  const mode = options?.mode ?? "exact";
  const hintOffset = options?.hintOffset;
  const wholeWord = options?.wholeWord === true;
  if (!needle) return null;

  if (mode === "exact") {
    const foldedHaystack = foldHaystackQuoteChars(haystack);
    const foldedNeedle = foldMatchingQuoteChars(needle);
    const matches = findAllIndexes(foldedHaystack, foldedNeedle).filter((index) =>
      !wholeWord || isWholeWordSpan(haystack, index, index + needle.length)
    );
    const best = chooseBestIndex(matches, (index) => index, hintOffset);
    if (best === -1) return null;
    return { start: best, end: best + needle.length };
  }

  const normalizedHaystack = normalizedHaystackBuilders[mode](haystack);
  const normalizedNeedle = buildNormalizedTextWithSourceIndices(needle, mode).normalized;
  if (!normalizedNeedle) return null;

  const matches = findAllIndexes(normalizedHaystack.normalized, normalizedNeedle);
  const best = chooseBestIndex(
    matches,
    (index) => normalizedHaystack.sourceIndices[index] ?? 0,
    hintOffset,
  );
  if (best === -1) return null;

  const start = normalizedHaystack.sourceIndices[best];
  const endIndexInclusive = best + normalizedNeedle.length - 1;
  const endSourceInclusive = normalizedHaystack.sourceIndices[endIndexInclusive];
  if (start === undefined || endSourceInclusive === undefined) return null;
  return expandMatchRangeForNeedleBoundaries(haystack, needle, { start, end: endSourceInclusive + 1 });
}

function firstIndexAtOrAfter(length: number, isAtOrAfter: (index: number) => boolean): number {
  let low = 0;
  let high = length;
  while (low < high) {
    const mid = (low + high) >>> 1;
    if (isAtOrAfter(mid)) high = mid;
    else low = mid + 1;
  }
  return low;
}

function offsetRange(range: TextMatchRange | null, offset: number): TextMatchRange | null {
  return range ? { start: range.start + offset, end: range.end + offset } : null;
}

/**
 * Returns a function equivalent to
 * `(searchStart) => findTextMatchRange(haystack.slice(searchStart), needle, { mode })` offset back into
 * `haystack`, but normalizes `haystack` once instead of on every call. Use it to walk successive matches.
 */
export function createForwardTextMatcher(
  haystack: string,
  needle: string,
  mode: TextMatchMode,
): (searchStart: number) => TextMatchRange | null {
  if (!needle) return () => null;

  if (mode === "exact") {
    const candidates = findAllIndexes(foldHaystackQuoteChars(haystack), foldMatchingQuoteChars(needle));
    return (searchStart) => {
      const index = candidates[firstIndexAtOrAfter(candidates.length, (i) => candidates[i] >= searchStart)];
      return index === undefined ? null : { start: index, end: index + needle.length };
    };
  }

  const normalizedNeedle = buildNormalizedTextWithSourceIndices(needle, mode).normalized;
  if (!normalizedNeedle) return () => null;

  // whitespace_agnostic collapses runs of "." and "*", so a slice that starts mid-run maps such a needle differently.
  if (mode === "whitespace_agnostic" && (normalizedNeedle.startsWith(".") || normalizedNeedle.startsWith("*"))) {
    return (searchStart) => offsetRange(findTextMatchRange(haystack.slice(searchStart), needle, { mode }), searchStart);
  }

  const { normalized, sourceIndices } = normalizedHaystackBuilders[mode](haystack);
  const candidates = findAllIndexes(normalized, normalizedNeedle);
  return (searchStart) => {
    const candidate = candidates[
      firstIndexAtOrAfter(candidates.length, (i) => sourceIndices[candidates[i]] >= searchStart)
    ];
    if (candidate === undefined) return null;
    const start = sourceIndices[candidate];
    const endSourceInclusive = sourceIndices[candidate + normalizedNeedle.length - 1];
    if (start === undefined || endSourceInclusive === undefined) return null;
    return expandMatchRangeForNeedleBoundaries(haystack, needle, { start, end: endSourceInclusive + 1 }, searchStart);
  };
}

export function countTextMatches(
  haystack: string,
  needle: string,
  options?: { mode?: TextMatchMode },
): number {
  const mode = options?.mode ?? "exact";
  if (!needle) return 0;

  if (mode === "exact") return findAllIndexes(haystack, needle).length;

  const normalizedHaystack = normalizedHaystackBuilders[mode](haystack).normalized;
  const normalizedNeedle = buildNormalizedTextWithSourceIndices(needle, mode).normalized;
  if (!normalizedNeedle) return 0;
  return findAllIndexes(normalizedHaystack, normalizedNeedle).length;
}
